import React, { useMemo, forwardRef, useRef, useImperativeHandle, useCallback } from 'react';
import {
  TablePaginatedV8,
  CellLeftV8,
  order2Sorting,
  TableCellV8,
  FormCol3,
  EditCellV8,
  CellCenterV8,
  TableDataState,
  promiseModalDialog,
  toastSuccess,
  CellDateTimeV8,
  CellDateV8,
  EyeColumnV8,
} from 'motion-components';
import { generatePath } from 'react-router-dom';
import { useHistory } from 'react-router';

import LoadingOverlay from '@achmadk/react-loading-overlay';
import { useRootStore } from 'store';
import { useTranslation } from 'react-i18next';
import {
  firstDateUserColumn,
  isActiveColumn,
  lastDateUserColumn,
  gridInactiveRowStyle,
  gridRowSelectedStyle,
  dateColumn,
} from 'shared-pages/common/grid-helpfunc';
import { FieldMultiMaterialDlg } from 'shared-pages/components';
import * as Lo from 'lodash';
import { FormInput, gqlAdd, gqlDelete, gqlUpdate } from '../graphql';
import { useMutation } from '@apollo/client';
import { getRouteByName } from 'app/routes';
import { useGetWasteDestinationTypeAsOptionsQuery } from 'shared-pages/components/field-select/fs-waste-destination-type';
import { IdocWasteDestinationTypes } from 'shared-pages/shared/constants';
import { isAfisCyprus, isRebattery } from 'shared-pages/common/constants';

/* ----------- Grid ------------------ */

type WasteReceptionPointRow = {
  id?: number;
  isActive?: boolean;
} & Record<string, unknown>;

type SortState = Array<{ id: string; desc: boolean }>;

type DataGridProps = {
  data: WasteReceptionPointRow[] | undefined;
  dataCount: number | undefined;
  initPageIndex: number | undefined;
  onPageChange: (pageIndex: number) => void;
  loading: boolean;
  handleExport: () => void;
  initOrderBy: string;
  onOrderChange: (orderBy: string) => void;
  selected: WasteReceptionPointRow | null;
  setSelected: (row: WasteReceptionPointRow | null) => void;
  refetch: () => void;
  isDA: boolean;
};

type SortableColumn = {
  id: string;
  orderByField?: string;
} & Record<string, unknown>;

export const DataGrid = forwardRef<unknown, DataGridProps>((props, ref) => {
  const {
    data,
    dataCount,
    initPageIndex,
    onPageChange,
    loading,
    handleExport,
    initOrderBy,
    onOrderChange,
    selected,
    setSelected,
    refetch,
  } = props;
  const history = useHistory();
  const { appStore, tableStore } = useRootStore();
  const { t, i18n } = useTranslation();
  const [updateWrecPoint] = useMutation(gqlUpdate);
  const [addNewWrecPoint] = useMutation(gqlAdd);
  const [deleteWrecPoint] = useMutation(gqlDelete);

  const rowClicked = useCallback(
    (row: WasteReceptionPointRow) => {
      history.push({
        pathname: generatePath(getRouteByName('WasteReceptionPoint'), { id: row.id }),
        state: {
          // pathname: location.pathname,
          // ...location.state,
        },
      });
    },
    [history],
  );
  const columns = useMemo<Array<SortableColumn>>(
    () => [
      EyeColumnV8(rowClicked),
      {
        id: 'id',
        header: t('ID'),
        columnName: t('ID'),
        accessorKey: 'id',
        orderByField: 'id',
        cell: CellCenterV8,
        size: 5,
      },
      {
        id: 'name',
        header: t('Επωνυμία'),
        columnName: t('Επωνυμία'),
        accessorKey: 'name',
        orderByField: 'name',
        cell: TableCellV8,
        size: 40,
        meta: { type: 'text' },
      },
      ...(!isRebattery
        ? [
            {
              id: 'code',
              header: t('Κωδικός'),
              columnName: t('Κωδικός'),
              accessorKey: 'code',
              orderByField: 'code',
              cell: TableCellV8,
              size: 40,
              meta: { type: 'text' },
            },
            {
              id: 'erpId',
              header: t('ERP ID'),
              columnName: t('ERP ID'),
              accessorKey: 'erpId',
              orderByField: 'erpId',
              cell: TableCellV8,
              size: 40,
              meta: { type: 'text' },
            },
          ]
        : []),
      /* {
        id: 'wasteDestinationTypeId',
        header: t('Τύπος Παραλαβής Αποβλήτων'),
        columnName: t('Τύπος Παραλαβής Αποβλήτων'),
        accessorKey: 'wasteDestinationTypeId',
        orderByField: 'wastedestinationtype.name',
        cell: TableCellV8,
        size: 50,
        meta: {
          type: 'select',
          hook: useGetWasteDestinationTypeAsOptionsQuery,
          idKey: 'wasteDestinationTypeId',
          idName: 'wastedestinationtype.name',
        },
      }, */
      {
        id: 'addressLine1',
        header: t('Διεύθυνση'),
        columnName: t('Διεύθυνση'),
        accessorKey: 'addressLine1',
        orderByField: 'addressLine1',
        cell: TableCellV8,
        size: 50,
        meta: { type: 'text' },
      },
      {
        id: 'addressCity',
        header: t('Πόλη'),
        columnName: t('Πόλη'),
        accessorKey: 'addressCity',
        orderByField: 'addressCity',
        cell: TableCellV8,
        size: 30,
        meta: { type: 'text' },
      },
      {
        id: 'phone',
        header: t('Τηλέφωνο'),
        columnName: t('Τηλέφωνο'),
        accessorKey: 'phone',
        orderByField: 'phone',
        cell: TableCellV8,
        size: 50,
        meta: { type: 'text' },
      },
      {
        id: 'companyVat',
        header: t('Α.Φ.Μ.'),
        columnName: t('Α.Φ.Μ.'),
        accessorKey: 'companyVat',
        orderByField: 'companyVat',
        cell: TableCellV8,
        size: 50,
        meta: { type: 'text' },
      },
      ...(!isAfisCyprus
        ? [
            {
              id: 'companyTaxOffice',
              header: t('Δ.Ο.Υ.'),
              columnName: t('Δ.Ο.Υ.'),
              accessorKey: 'companyTaxOffice',
              orderByField: 'companyTaxOffice',
              cell: TableCellV8,
              size: 50,
              meta: { type: 'text' },
            },
          ]
        : []),
      {
        id: 'contractExpDate',
        header: t('Λήξη Σύμβασης'),
        columnName: t('Λήξη Σύμβασης'),
        accessorKey: 'contractExpDate',
        orderByField: 'contractExpDate',
        ...dateColumn(t),
      },
      ...(!isRebattery
        ? [
            {
              id: 'collector.name',
              header: t('Συλλέκτης'),
              columnName: t('Συλλέκτης'),
              accessorKey: 'collector.name',
              orderByField: 'collector.name',
              cell: TableCellV8,
              size: 20,
            },
          ]
        : []),
      isActiveColumn(t, i18n),
      {
        id: 'edit',
        header: t('Ενέργειες'),
        columnName: t('Ενέργειες'),
        accessorKey: 'edit',
        orderByField: 'lastUser',
        size: 120,
        cell: ({ row, table }) => {
          return true ? EditCellV8({ row, table }) : <>-</>;
        },
      },
    ],
    [i18n, rowClicked, t],
  );

  const tableRef = useRef(null);
  // return of useRef
  useImperativeHandle(ref, () => (tableRef.current ? tableRef.current : null));

  return (
    <LoadingOverlay active={loading} spinner text={'Παρακαλώ περιμένετε...'}>
      {data ? (
        <TablePaginatedV8
          ref={tableRef}
          loading={loading}
          id="containers-grid"
          appStore={appStore}
          tableStore={tableStore}
          key="grid"
          columns={columns as any}
          data={data}
          dataCount={dataCount}
          topBorderRadius={false}
          onRowClick={(table: { row: { original: WasteReceptionPointRow } }) =>
            setSelected(table.row.original)
          }
          rowProps={(row: { original: WasteReceptionPointRow }) => {
            let style = undefined;
            if (row.original.id === selected?.id) style = gridRowSelectedStyle;
            if (row.original.isActive === false) style = { ...style, ...gridInactiveRowStyle };
            return { style: style };
          }}
          onExport={handleExport}
          onPageChange={onPageChange}
          initPageIndex={initPageIndex}
          initSorting={order2Sorting(initOrderBy)}
          onSortByChange={(sort: SortState) => {
            console.log('$$$$ grid sortbychange', sort);
            // $$$
            const columnId = sort[0].id;
            const sortOrder = sort[0].desc ? 'DESC' : 'ASC';
            // find column by id
            const cols = columns.filter((r) => r.id === columnId);
            if (cols.length === 1) {
              const col = cols[0] as any;
              let order = col.orderByField ? col.orderByField : 'id';
              order = order + ' ' + sortOrder;
              onOrderChange(order);
            }
          }}
        />
      ) : (
        <div style={{ height: '200px' }}></div>
      )}
    </LoadingOverlay>
  );
});
